import { ReadFile } from '@paragon/client-tools';
import { Bearing, ComponentDesign, RoofContainer, TrussEnvelope } from '@paragon/contract-library';
import { closeCurrentPopup } from '@paragon/popup-state';

import { ChooseProjectDialogProps } from '../../components/dialogs/choose-project/ChooseProjectDialog';
import { ConfirmAffectAllLocksDialogProps } from '../../components/dialogs/ConfirmAffectAllLocksDialog';
import {
  ProjectWideSettingsDialogProps,
  SettingsDialogTab,
} from '../../components/dialogs/project-wide-settings/ProjectWideSettingsDialog';
import { showPopup } from '../slices/popupSlice';
import { ThunkActionReturn, ThunkActionVoid } from './thunkActions';

export const USER_SETTINGS_DIALOG = 'USER_SETTINGS_DIALOG';
export const COMPONENT_DELETION_CONFIRMATION_DIALOG = 'COMPONENT_DELETION_CONFIRMATION_DIALOG';
export const DELETE_ROOF_CONTAINER_CONFIRMATION_DIALOG = 'DELETE_ROOF_CONTAINER_CONFIRMATION_DIALOG';
export const CARRIED_BEARING_DELETION_CONFIRMATION_DIALOG = 'CARRIED_BEARING_DELETION_CONFIRMATION_DIALOG';
export const PARENT_TRUSS_GROUP_DELETION_CONFIRMATION_DIALOG = 'PARENT_TRUSS_GROUP_DELETION_CONFIRMATION_DIALOG';
export const SELECT_STATION_DIALOG = 'SELECT_STATION_DIALOG';
export const SELECT_SCHEDULE_LINE_GROUP_DIALOG = 'SELECT_SCHEDULE_LINE_GROUP_DIALOG';
export const IMPORT_ASSEMBLY_GROUP_DIALOG = 'IMPORT_ASSEMBLY_GROUP_DIALOG';
export const PARTS_DIALOG = 'PARTS_AND_BUNDLE_VIEW_DIALOG';
export const INSERTION_MENU_DIALOG = 'INSERTION_MENU_DIALOG';
export const FLIP_TRUSS_ENVELOPE_DIALOG = 'FLIP_TRUSS_ENVELOPE_DIALOG';
export const SELECT_COMPONENT_DESIGN_DIALOG = 'SELECT_COMPONENT_DESIGN_DIALOG';
export const CONFIRM_CANCEL_INSERTION_DIALOG = 'CONFIRM_CANCEL_INSERTION_DIALOG';
export const CONFIRM_REPLACE_LAYOUT_DIALOG = 'CONFIRM_REPLACE_LAYOUT_DIALOG';
export const CONFIRM_AFFECT_ALL_LOCKS_DIALOG = 'CONFIRM_AFFECT_ALL_LOCKS_DIALOG';
export const QUALITY_CONTROL_PDF_DOWNLOAD_DIALOG = 'QUALITY_CONTROL_PDF_DOWNLOAD_DIALOG';
export const QUALITY_CONTROL_DIGITAL_QC_DOWNLOAD_DIALOG = 'QUALITY_CONTROL_DIGITAL_QC_DOWNLOAD_DIALOG';
export const CREATE_ESTIMATE_DIALOG = 'CREATE_ESTIMATE_DIALOG';
export const HOTKEY_DIALOG = 'HOTKEY_DIALOG';
export const LOG_IN_DIALOG = 'LOG_IN_DIALOG';
export const SIGN_UP_DIALOG = 'SIGN_UP_DIALOG';
export const SHARE_DIALOG = 'SHARE_DIALOG';
export const CHOOSE_PROJECT_DIALOG = 'CHOOSE_PROJECT_DIALOG';
export const WELCOME_DIALOG = 'WELCOME_DIALOG';
export const SHARED_COMPONENT_SET_INFO_DIALOG = 'SHARED_COMPONENT_SET_INFO_DIALOG';
export const EDIT_DEPTH_DIALOG = 'EDIT_DEPTH_DIALOG';
export const NOT_ON_MOBILE_DIALOG = 'NOT_ON_MOBILE_DIALOG';
export const RENAME_SINGLE_COMPONENT_DESIGN_DIALOG = 'RENAME_SINGLE_COMPONENT_DESIGN_DIALOG';
export const PROJECT_WIDE_SETTINGS_DIALOG = 'PROJECT_WIDE_SETTINGS_DIALOG';
export const UPGRADE_ACCOUNT_DIALOG = 'UPGRADE_ACCOUNT_DIALOG';
export const DOWNLOADS_DIALOG = 'DOWNLOADS_DIALOG';

export const SELECT_PLATE_LISTS_DIALOG = 'SELECT_PLATE_LISTS_DIALOG';
export const PRESETS_DIALOG = 'PRESETS_DIALOG';
export const BEARING_IMAGE_UPLOAD_DIALOG = 'BEARING_IMAGE_UPLOAD_DIALOG';

export interface PopupOptionsMap {
  [USER_SETTINGS_DIALOG]: undefined;
  [COMPONENT_DELETION_CONFIRMATION_DIALOG]: string[];
  [DELETE_ROOF_CONTAINER_CONFIRMATION_DIALOG]: { readonly projectGuid: string; readonly roofContainer: RoofContainer };
  [CARRIED_BEARING_DELETION_CONFIRMATION_DIALOG]: CarriedBearingDeleteConfirmationProps;
  [PARENT_TRUSS_GROUP_DELETION_CONFIRMATION_DIALOG]: ParentTrussGroupDeleteConfirmationProps;
  [QUALITY_CONTROL_PDF_DOWNLOAD_DIALOG]: undefined;
  [QUALITY_CONTROL_DIGITAL_QC_DOWNLOAD_DIALOG]: undefined;
  [SELECT_STATION_DIALOG]: undefined;
  [SELECT_SCHEDULE_LINE_GROUP_DIALOG]: undefined;
  [IMPORT_ASSEMBLY_GROUP_DIALOG]: undefined;
  [PARTS_DIALOG]: undefined;
  [INSERTION_MENU_DIALOG]: undefined;
  [FLIP_TRUSS_ENVELOPE_DIALOG]: FlipTrussEnvelopeDialogProps;
  [SELECT_COMPONENT_DESIGN_DIALOG]: SelectComponentDesignDialogProps;
  [CONFIRM_CANCEL_INSERTION_DIALOG]: undefined;
  [CONFIRM_REPLACE_LAYOUT_DIALOG]: ConfirmReplaceLayoutDialogProps;
  [CONFIRM_AFFECT_ALL_LOCKS_DIALOG]: ConfirmAffectAllLocksDialogProps;
  [CREATE_ESTIMATE_DIALOG]: undefined;
  [HOTKEY_DIALOG]: undefined;
  [LOG_IN_DIALOG]: undefined;
  [SHARE_DIALOG]: undefined;
  [SIGN_UP_DIALOG]: undefined;
  [CHOOSE_PROJECT_DIALOG]: ChooseProjectDialogProps;
  [WELCOME_DIALOG]: undefined;
  [SHARED_COMPONENT_SET_INFO_DIALOG]: undefined;
  [EDIT_DEPTH_DIALOG]: undefined;
  [NOT_ON_MOBILE_DIALOG]: {
    readonly featureText: string;
  };
  [RENAME_SINGLE_COMPONENT_DESIGN_DIALOG]: { componentDesignGuid: string };
  [PROJECT_WIDE_SETTINGS_DIALOG]: ProjectWideSettingsDialogProps;
  [UPGRADE_ACCOUNT_DIALOG]: { message?: string };
  [DOWNLOADS_DIALOG]: undefined;

  [SELECT_PLATE_LISTS_DIALOG]: undefined;
  [PRESETS_DIALOG]: undefined;
  [BEARING_IMAGE_UPLOAD_DIALOG]: { imageUrl: string };
}

export function showUserSettingsDialog() {
  return showPopup(USER_SETTINGS_DIALOG);
}

export interface ComponentDesignConfirmationProps {
  componentDesignGuids: string[];
}

export interface RoofContainerConfirmationProps {
  projectGuid: string;
  roofContainer: RoofContainer;
}

export interface CarriedBearingDeleteConfirmationProps {
  readonly componentDesign: ComponentDesign;
  readonly bearing: Bearing;
}

export interface ParentTrussGroupDeleteConfirmationProps {
  readonly parentTrussGroupGuidsBeingDeleted: readonly string[];
  readonly childTrussGroupsWhichWillBeCascadedDeleted: readonly string[];
}

export function showDeleteComponentConfirmationDialog(componentDesignGuids: string[]) {
  return showPopup(COMPONENT_DELETION_CONFIRMATION_DIALOG, componentDesignGuids);
}

export function showDeleteRoofContainerConfirmationDialog(projectGuid: string, roofContainer: RoofContainer) {
  return showPopup(DELETE_ROOF_CONTAINER_CONFIRMATION_DIALOG, { projectGuid, roofContainer });
}

export function showDeleteCarriedBearingConfirmationDialog(componentDesign: ComponentDesign, bearing: Bearing) {
  return showPopup(CARRIED_BEARING_DELETION_CONFIRMATION_DIALOG, { componentDesign, bearing });
}

export function showDeleteTrussGroupsConfirmationDialog(
  parentTrussGroupGuidsBeingDeleted: readonly string[],
  childTrussGroupsWhichWillBeCascadedDeleted: readonly string[],
) {
  return showPopup(PARENT_TRUSS_GROUP_DELETION_CONFIRMATION_DIALOG, {
    parentTrussGroupGuidsBeingDeleted,
    childTrussGroupsWhichWillBeCascadedDeleted,
  });
}

export function showQualityControlPdfDownloadDialog() {
  return showPopup(QUALITY_CONTROL_PDF_DOWNLOAD_DIALOG);
}

export function showQualityControlDigitalQcDownloadDialog() {
  return showPopup(QUALITY_CONTROL_DIGITAL_QC_DOWNLOAD_DIALOG);
}

export function showSelectStationDialog() {
  return showPopup(SELECT_STATION_DIALOG);
}

export function showSelectScheduleLineGroupDialog() {
  return showPopup(SELECT_SCHEDULE_LINE_GROUP_DIALOG);
}

export function showImportAssemblyGroupDialog() {
  return showPopup(IMPORT_ASSEMBLY_GROUP_DIALOG);
}

export function showPartsDialog() {
  return showPopup(PARTS_DIALOG);
}

export function showInsertionMenuDialog() {
  return showPopup(INSERTION_MENU_DIALOG);
}

export interface FlipTrussEnvelopeDialogProps {
  flippedEnvelopes: readonly TrussEnvelope[];
}

export function showFlipTrussEnvelopeDialog(props: FlipTrussEnvelopeDialogProps) {
  return showPopup(FLIP_TRUSS_ENVELOPE_DIALOG, props);
}

export interface SelectComponentDesignDialogProps {
  continuation: {
    type: 'associate-with-envelope';
    trussEnvelopeGuids: string[];
  };
}

export function showSelectComponentDesignDialog(props: SelectComponentDesignDialogProps) {
  return showPopup(SELECT_COMPONENT_DESIGN_DIALOG, props);
}

export function showConfirmCancelInsertionDialog() {
  return showPopup(CONFIRM_CANCEL_INSERTION_DIALOG);
}

export interface ConfirmReplaceLayoutDialogProps {
  readonly file: ReadFile;
  readonly layoutOnly: boolean;
}

export function showConfirmReplaceLayoutDialog(props: ConfirmReplaceLayoutDialogProps) {
  return showPopup(CONFIRM_REPLACE_LAYOUT_DIALOG, props);
}

export function showConfirmAffectAllLocksDialog(props: ConfirmAffectAllLocksDialogProps) {
  return showPopup(CONFIRM_AFFECT_ALL_LOCKS_DIALOG, props);
}

export function showCreateEstimateDialog() {
  return showPopup(CREATE_ESTIMATE_DIALOG);
}

export const toggleHotkeyDialog = (): ThunkActionVoid => (dispatch, getState) => {
  if (getState().popup.popupName === HOTKEY_DIALOG) dispatch(closeCurrentPopup());
  else dispatch(showPopup(HOTKEY_DIALOG));
};

export function showLogInDialog() {
  return showPopup(LOG_IN_DIALOG);
}

export function showSignUpDialog() {
  return showPopup(SIGN_UP_DIALOG);
}

export function showShareDialog() {
  return showPopup(SHARE_DIALOG);
}

export function showChooseProjectDialog(options?: ChooseProjectDialogProps) {
  return showPopup(CHOOSE_PROJECT_DIALOG, { ...options });
}

export function showWelcomeDialog() {
  return showPopup(WELCOME_DIALOG);
}

export function showSharedComponentSetInfoDialog() {
  return showPopup(SHARED_COMPONENT_SET_INFO_DIALOG);
}

export function showEditDepthDialog() {
  return showPopup(EDIT_DEPTH_DIALOG);
}

export function showNotOnMobileDialog(featureText: string) {
  return showPopup(NOT_ON_MOBILE_DIALOG, { featureText });
}

export function showProjectWideSettingsDialog(
  initialTab: SettingsDialogTab,
  initiallySelectedComponentDesignGuids: readonly string[],
) {
  return showPopup(PROJECT_WIDE_SETTINGS_DIALOG, { initialTab, initiallySelectedComponentDesignGuids });
}

export function showUpgradeAccountDialog(message?: string) {
  return showPopup(UPGRADE_ACCOUNT_DIALOG, { message });
}

export const showUpgradeAccountDialogIfQuotaMetAndNotAllowedToExceed =
  (anticipatedUsage?: number, message?: string): ThunkActionReturn<boolean> =>
  (dispatch, getState) => {
    const usageData = getState().usageData;
    if (
      usageData &&
      usageData.quotaPerMonth !== null &&
      (usageData.usageThisMonth >= usageData.quotaPerMonth ||
        (anticipatedUsage !== undefined && usageData.usageThisMonth + anticipatedUsage > usageData.quotaPerMonth)) &&
      !usageData.allowedToExceed
    ) {
      dispatch(showUpgradeAccountDialog(message));
      return true;
    }

    return false;
  };

export const showDownloadsDialog = (): ThunkActionVoid => (dispatch) => {
  dispatch(showPopup(DOWNLOADS_DIALOG));
};

export const showSelectPlateListsDialog = (): ThunkActionVoid => (dispatch) => {
  dispatch(showPopup(SELECT_PLATE_LISTS_DIALOG));
};

export const showPresetsDialog = (): ThunkActionVoid => (dispatch) => {
  dispatch(showPopup(PRESETS_DIALOG));
};

export const showBearingImageUploadDialog =
  (imageUrl: string): ThunkActionVoid =>
  (dispatch) => {
    dispatch(showPopup(BEARING_IMAGE_UPLOAD_DIALOG, { imageUrl }));
  };
